import React, { useState, useEffect } from 'react';
import { Plus, Edit3, Trash2, X, Check, Calendar } from 'lucide-react';

// Framer Motion alternative using CSS animations
const AnimatedDiv = ({ children, className, delay = 0, ...props }) => {
  const [isVisible, setIsVisible] = useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div
      className={`${className} transition-all duration-500 transform ${isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-95'
        }`}
      {...props}
    >
      {children}
    </div>
  );
};

const StickyNotes = ({ isOpen, setIsOpen }) => {
  // Default notes - will be used if no localStorage data exists
  const defaultNotes = [
    {
      id: 1,
      title: "The beginning of business design, If you're to be one, then you start by your action, Architect",
      description: "Key principles for starting a business design process",
      reminderDate: "2024-06-15",
      status: "Done"
    },
    {
      id: 2,
      title: "15 Things You Should Give Up To Be Happy",
      description: "Personal development guide for happiness",
      reminderDate: "2024-06-20",
      status: "In Progress"
    },
    {
      id: 3,
      title: "The Psychology of Money",
      description: "Understanding financial behavior and decision making",
      reminderDate: "2024-06-25",
      status: "Yet to Start"
    }
  ];

  const [notes, setNotes] = useState(defaultNotes);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    reminderDate: '',
    status: 'Yet to Start'
  });

  // Load notes from localStorage on component mount
  useEffect(() => {
    try {
      const savedNotes = localStorage.getItem('stickyNotes');
      if (savedNotes) {
        const parsedNotes = JSON.parse(savedNotes);
        if (Array.isArray(parsedNotes) && parsedNotes.length > 0) {
          setNotes(parsedNotes);
        }
      }
    } catch (error) {
      console.error('Error loading notes from localStorage:', error);
      // If there's an error, keep the default notes
    }
  }, []);

  // Save notes to localStorage whenever notes change
  useEffect(() => {
    try {
      localStorage.setItem('stickyNotes', JSON.stringify(notes));
    } catch (error) {
      console.error('Error saving notes to localStorage:', error);
    }
  }, [notes]);

  const statusColors = {
    'Done': 'bg-gradient-to-br from-green-100 to-green-200 border-green-300 shadow-green-100',
    'In Progress': 'bg-gradient-to-br from-blue-100 to-blue-200 border-blue-300 shadow-blue-100',
    'Yet to Start': 'bg-gradient-to-br from-yellow-100 to-yellow-200 border-yellow-300 shadow-yellow-100',
    'Due': 'bg-gradient-to-br from-red-100 to-red-200 border-red-300 shadow-red-100'
  };

  const statusOptions = ['Yet to Start', 'In Progress', 'Done', 'Due'];

  const formatDisplayDate = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const openCreateModal = () => {
    setModalMode('create');
    setFormData({
      title: '',
      description: '',
      reminderDate: getTodayDate(),
      status: 'Yet to Start'
    });
    setShowModal(true);
  };

  const openEditModal = (note) => {
    setModalMode('edit');
    setEditingNoteId(note.id);
    setFormData({
      title: note.title,
      description: note.description,
      reminderDate: note.reminderDate,
      status: note.status
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setTimeout(() => {
      setModalMode('create');
      setEditingNoteId(null);
      setFormData({
        title: '',
        description: '',
        reminderDate: '',
        status: 'Yet to Start'
      });
    }, 200);
  };

  const handleSave = () => {
    if (formData.title.trim() && formData.reminderDate) {
      if (modalMode === 'create') {
        const newNote = {
          id: Date.now(),
          ...formData
        };
        setNotes([newNote, ...notes]);
      } else {
        setNotes(notes.map(note =>
          note.id === editingNoteId
            ? { ...note, ...formData }
            : note
        ));
      }
      closeModal();
    }
  };

  const handleDeleteNote = (id) => {
    setDeletingId(id);
    setTimeout(() => {
      setNotes(notes.filter(note => note.id !== id));
      setDeletingId(null);
    }, 300);
  };

  // Function to clear all notes and reset to defaults
  const resetToDefault = () => {
    setNotes(defaultNotes);
    localStorage.setItem('stickyNotes', JSON.stringify(defaultNotes));
  };

  // Function to clear all notes
  const clearAllNotes = () => {
    setNotes([]);
    localStorage.removeItem('stickyNotes');
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Main Container with backdrop blur */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4 animate-fadeIn">
        <div className="w-full max-w-6xl h-full max-h-[90vh] bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 overflow-hidden flex flex-col animate-slideUp">

          {/* Header with glassmorphism effect */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200/50 bg-white/80 backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl sm:text-3xl font-bold ml-4 bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                Sticky Notes
              </h1>
              <span className="text-sm text-gray-500 ml-2">({notes.length} notes)</span>
            </div>
            <div className="flex items-center gap-2">
              {/* Reset button */}
              <button
                onClick={resetToDefault}
                title="Reset to default notes"
                className="p-2 hover:bg-gray-100/80 rounded-full transition-all duration-200 hover:scale-105"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-gray-100/80 rounded-full transition-all duration-200 hover:scale-105 hover:rotate-90"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content with staggered animations */}
          <div className="flex-1 overflow-auto p-4 sm:p-6 bg-gradient-to-br from-gray-50/50 to-white/80">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">

              {/* Add New Note Button */}
              <AnimatedDiv delay={50}>
                <div
                  onClick={openCreateModal}
                  className="bg-gradient-to-br from-gray-50 to-gray-100/80 border-2 border-dashed border-gray-300 rounded-xl p-4 sm:p-6 min-h-[200px] flex flex-col items-center justify-center cursor-pointer hover:from-blue-50 hover:to-blue-100/80 hover:border-blue-400 transition-all duration-300 group hover:shadow-lg hover:shadow-blue-100/50 hover:scale-105"
                >
                  <Plus className="w-8 h-8 text-gray-400 group-hover:text-blue-500 mb-3 transition-all duration-300 group-hover:scale-110 group-hover:rotate-90" />
                  <span className="text-gray-500 group-hover:text-blue-600 font-medium transition-colors duration-300">
                    Add New Note
                  </span>
                </div>
              </AnimatedDiv>

              {/* Notes with staggered entrance */}
              {notes.map((note, index) => (
                <AnimatedDiv key={note.id} delay={100 + index * 50}>
                  <div
                    className={`${statusColors[note.status]} rounded-xl p-4 sm:p-6 min-h-[200px] relative group shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 hover:-rotate-1 ${deletingId === note.id ? 'animate-slideOut opacity-0 scale-75' : ''
                      }`}
                  >
                    {/* Action buttons with better positioning */}
                    <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                      <button
                        onClick={() => openEditModal(note)}
                        className="p-2 bg-white/90 hover:bg-white rounded-full shadow-md transition-all duration-200 hover:scale-110 hover:shadow-lg"
                      >
                        <Edit3 className="w-4 h-4 text-gray-600 hover:text-blue-600 transition-colors" />
                      </button>
                      <button
                        onClick={() => handleDeleteNote(note.id)}
                        className="p-2 bg-white/90 hover:bg-white rounded-full shadow-md transition-all duration-200 hover:scale-110 hover:shadow-lg"
                      >
                        <Trash2 className="w-4 h-4 text-gray-600 hover:text-red-600 transition-colors" />
                      </button>
                    </div>

                    <h3 className="text-sm font-semibold mb-3 pr-16 leading-relaxed text-gray-800">
                      {note.title}
                    </h3>

                    {note.description && (
                      <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                        {note.description}
                      </p>
                    )}

                    <div className="absolute bottom-4 left-4 right-4 flex justify-between items-center">
                      <div className="flex items-center gap-2 text-xs text-gray-600 font-medium bg-white/60 px-2 py-1 rounded-full">
                        <Calendar className="w-3 h-3" />
                        {formatDisplayDate(note.reminderDate)}
                      </div>
                      <span className={`text-xs px-3 py-1 rounded-full font-medium backdrop-blur-sm shadow-sm ${note.status === 'Done' ? 'bg-green-500/20 text-green-800 border border-green-500/30' :
                        note.status === 'In Progress' ? 'bg-blue-500/20 text-blue-800 border border-blue-500/30' :
                          note.status === 'Yet to Start' ? 'bg-yellow-500/20 text-yellow-800 border border-yellow-500/30' :
                            'bg-red-500/20 text-red-800 border border-red-500/30'
                        }`}>
                        {note.status}
                      </span>
                    </div>
                  </div>
                </AnimatedDiv>
              ))}

              {/* Show message when no notes */}
              {notes.length === 0 && (
                <div className="col-span-full flex flex-col items-center justify-center py-12 text-gray-500">
                  <div className="text-6xl mb-4">📝</div>
                  <h3 className="text-xl font-semibold mb-2">No notes yet</h3>
                  <p className="text-sm text-center max-w-md">
                    Create your first note to get started with organizing your tasks and ideas.
                  </p>
                  <button
                    onClick={openCreateModal}
                    className="mt-4 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Create First Note
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Modal with better animations */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-fadeIn">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 w-full max-w-2xl max-h-[80vh] overflow-auto animate-modalSlideUp">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200/50 bg-gradient-to-r from-white/80 to-gray-50/80">
              <h2 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                {modalMode === 'create' ? 'Create New Note' : 'Edit Note'}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-gray-100/80 rounded-full transition-all duration-200 hover:scale-110 hover:rotate-90"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter your task title..."
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 bg-white/80 placeholder-gray-400"
                  maxLength={100}
                />
                <div className="text-xs text-gray-500 flex justify-between">
                  <span>{formData.title.length}/100 characters</span>
                  {formData.title.length > 80 && (
                    <span className="text-orange-500 font-medium">Almost at limit!</span>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Description
                </label>
                <textarea
                  placeholder="Add more details about your task..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full p-4 border-2 border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 bg-white/80 placeholder-gray-400"
                  rows="2"
                  maxLength={100}
                />
                <div className="text-xs text-gray-500 flex justify-between">
                  <span>{formData.description.length}/100 characters</span>
                  {formData.description.length > 80 && (
                    <span className="text-orange-500 font-medium">Almost at limit!</span>
                  )}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <div className="space-y-2 flex-1">
                  <label className="block text-sm font-semibold text-gray-700">
                    Reminder Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.reminderDate}
                    onChange={(e) => setFormData({ ...formData, reminderDate: e.target.value })}
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 bg-white/80"
                  />
                </div>

                <div className="space-y-2 flex-1">
                  <label className="block text-sm font-semibold text-gray-700">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 bg-white/80"
                  >
                    {statusOptions.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex gap-3 p-6 border-t border-gray-200/50 bg-gradient-to-r from-gray-50/80 to-white/80">
              <button
                onClick={closeModal}
                className="flex-1 py-3 px-4 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 font-semibold hover:scale-105"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!formData.title.trim() || !formData.reminderDate}
                className="flex-1 py-3 px-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 font-semibold flex items-center justify-center gap-2 hover:scale-105 disabled:hover:scale-100 shadow-lg hover:shadow-xl"
              >
                <Check className="w-4 h-4" />
                {modalMode === 'create' ? 'Create Note' : 'Update Note'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from { 
            opacity: 0; 
            transform: translateY(20px) scale(0.95); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0) scale(1); 
          }
        }
        
        @keyframes slideOut {
          from { 
            opacity: 1; 
            transform: translateY(0) scale(1); 
          }
          to { 
            opacity: 0; 
            transform: translateY(-20px) scale(0.8); 
          }
        }
        
        @keyframes modalSlideUp {
          from { 
            opacity: 0; 
            transform: translateY(30px) scale(0.9); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0) scale(1); 
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        
        .animate-slideUp {
          animation: slideUp 0.4s ease-out;
        }
        
        .animate-slideOut {
          animation: slideOut 0.3s ease-in;
        }
        
        .animate-modalSlideUp {
          animation: modalSlideUp 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default StickyNotes;